import React from 'react';
import { Typography, Grid, Box, Card, CardContent, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import VerifiedIcon from '@mui/icons-material/Verified';
import ContactMailIcon from '@mui/icons-material/ContactMail';

const GetStartedSection = () => {
  return (
    <section id="getstarted" className="whitepaper-section">
      <Box sx={{
        background: 'linear-gradient(135deg, rgba(96,165,250,0.1) 0%, rgba(139,92,246,0.1) 50%, rgba(236,72,153,0.1) 100%)',
        border: '2px solid rgba(96,165,250,0.3)',
        borderRadius: '32px',
        p: { xs: 4, md: 6 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(96,165,250,0.15) 0%, transparent 70%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ 
            mb: 3, 
            fontWeight: 800, 
            fontSize: { xs: '2rem', md: '3rem' },
            background: 'linear-gradient(135deg, #60a5fa 0%, #8b5cf6 50%, #ec4899 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.2
          }}>
            Ready to Transform Your Security Data Architecture?
          </Typography>
          <Typography variant="h5" sx={{ 
            mb: 5, 
            color: '#94a3b8',
            fontSize: { xs: '1.1rem', md: '1.4rem' },
            maxWidth: '800px',
            mx: 'auto'
          }}>
            Start with a free assessment to understand your optimization potential
          </Typography>
          
          <Grid container spacing={4} sx={{ mb: 5, maxWidth: '1200px', mx: 'auto' }}>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '20px',
                p: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-12px)',
                  boxShadow: '0 20px 60px rgba(96,165,250,0.4)',
                  borderColor: 'rgba(96,165,250,0.6)',
                  background: 'linear-gradient(135deg, rgba(96,165,250,0.2) 0%, rgba(59,130,246,0.15) 100%)'
                }
              }}>
                <Box sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 32px rgba(96,165,250,0.5)'
                }}>
                  <BarChartIcon sx={{ fontSize: '2.5rem', color: '#fff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#60a5fa' }}>
                  Cost Assessment
                </Typography>
                <Typography sx={{ color: '#b0bec5', fontSize: '1.05rem', lineHeight: 1.7 }}>
                  Analyze your current SIEM spending and identify optimization opportunities
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '20px',
                p: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-12px)',
                  boxShadow: '0 20px 60px rgba(16,185,129,0.4)',
                  borderColor: 'rgba(16,185,129,0.6)',
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)'
                }
              }}>
                <Box sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 32px rgba(16,185,129,0.5)'
                }}>
                  <ArchitectureIcon sx={{ fontSize: '2.5rem', color: '#fff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#10b981' }}>
                  Architecture Workshop
                </Typography>
                <Typography sx={{ color: '#b0bec5', fontSize: '1.05rem', lineHeight: 1.7 }}>
                  Design your Security Data Fabric with our solution architects
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
                border: '2px solid rgba(245,158,11,0.3)',
                borderRadius: '20px',
                p: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-12px)',
                  boxShadow: '0 20px 60px rgba(245,158,11,0.4)',
                  borderColor: 'rgba(245,158,11,0.6)',
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(217,119,6,0.15) 100%)'
                }
              }}>
                <Box sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 32px rgba(245,158,11,0.5)'
                }}>
                  <VerifiedIcon sx={{ fontSize: '2.5rem', color: '#fff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#f59e0b' }}>
                  Proof of Concept
                </Typography>
                <Typography sx={{ color: '#b0bec5', fontSize: '1.05rem', lineHeight: 1.7 }}>
                  Validate performance and cost savings in your environment
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Button 
            variant="contained" 
            size="large"
            onClick={() => window.location.href = '/contact'}
            sx={{
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              color: '#fff',
              fontSize: '1.2rem',
              fontWeight: 700,
              px: 6,
              py: 2,
              borderRadius: '12px',
              textTransform: 'none',
              boxShadow: '0 8px 32px rgba(96,165,250,0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 48px rgba(96,165,250,0.6)'
              }
            }}
          >
            Contact Sales
          </Button>
        </Box>
      </Box>

      <Card sx={{ 
        mt: 5,
        background: 'linear-gradient(135deg, rgba(10,34,64,0.6) 0%, rgba(10,34,64,0.4) 100%)',
        border: '2px solid rgba(96,165,250,0.2)',
        borderRadius: '24px',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Box sx={{ 
            textAlign: 'center', 
            mb: 4,
            pb: 3,
            borderBottom: '2px solid rgba(96,165,250,0.2)'
          }}>
            <Typography variant="h4" sx={{ 
              color: '#60a5fa', 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 1
            }}>
              Key Questions for Your Assessment
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '1.1rem' }}>
              Help us understand your security data environment
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              'What is your current daily security data volume?',
              'What is your annual SIEM licensing cost?',
              'How many security data sources do you have?',
              'What are your compliance retention requirements?',
              'Are you experiencing SIEM performance issues?',
              'Do you need multi-SIEM or hybrid cloud deployment?',
              'What is your average security event investigation time?',
              'How many false positive alerts does your SOC handle daily?'
            ].map((question, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'flex-start',
                  p: 2,
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(96,165,250,0.05)',
                    transform: 'translateX(8px)'
                  }
                }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 4px 16px rgba(16,185,129,0.3)'
                  }}>
                    <CheckCircleIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ 
                    color: '#e0e7ef',
                    fontSize: '1.05rem',
                    lineHeight: 1.6,
                    pt: 0.3
                  }}>
                    {question}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </section>
  );
};

export default GetStartedSection;
